import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import {
  getStopPortLabel,
  getStopPrimaryDateTime,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/reviewScheduleFormatters";

export type ReviewStopDisplay = {
  primaryDateTime: string;
  portLabel?: string;
  addressName?: string;
  streetLine?: string;
  regionLine?: string;
};

/**
 * Builds the pickup/delivery cell content for the multi-stop review packing list table,
 * keyed by `stop_key`. Reuses the schedule review formatters so both sections stay in sync.
 */
export function useReviewStopDisplay(): {
  isMultiStop: boolean;
  stopDisplayByKey: Map<string, ReviewStopDisplay>;
} {
  const intl = useIntl();
  const stops = useWatch({ name: "schedule.stops" }) as ScheduleStopValue[] | undefined;

  return useMemo(() => {
    const allStops = stops ?? [];
    const isMultiStop = allStops.length > 2;
    const stopDisplayByKey = new Map<string, ReviewStopDisplay>();

    allStops.forEach((stop) => {
      if (!stop?.stop_key) return;

      const streetLine = [stop.street_address, stop.street_address_line_two].filter(Boolean).join(", ");
      const regionLine = [stop.city_name, stop.state_name, stop.country_name, stop.zipcode].filter(Boolean).join(", ");

      stopDisplayByKey.set(stop.stop_key, {
        primaryDateTime: getStopPrimaryDateTime(stop, intl),
        portLabel: getStopPortLabel(stop) || undefined,
        addressName: stop.address_name?.trim() || undefined,
        streetLine: streetLine || undefined,
        regionLine: regionLine || undefined,
      });
    });

    return { isMultiStop, stopDisplayByKey };
  }, [stops, intl]);
}
